import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, isValid, parseISO } from "date-fns";
import { PaginationState } from "@tanstack/react-table";

import Loader from "../../../shared/Ui/Loader/Loader";
import { EmptyListPlaceholderContent } from "../../../shared/Ui/EmptyPageBlock";
import StatusBadge from "../../../shared/Ui/StatusBadge/StatusBadge";
import Pagination from "../../../shared/components/Table/Pagination";
import { useAppSelector } from "../../../shared/hook";
import {
  MarkingCodeReportRow,
  MarkingCodesKmStatus,
  MarkingCodesPackagingType,
  MarkingCodesReportItem,
  MarkingCodesReportPreviewResponse,
  downloadMarkingCodesReport,
  getMarkingCodesReport,
  previewMarkingCodesReport,
} from "../../../common/api/markingCodesReportApi";
import { downloadXlsxBlob } from "../../Billing/billingUtils";
import { KM_STATUS_LABEL_KEYS, PACKAGING_LABEL_KEYS } from "./markingCodesReportUtils";
import { useProductGroupOptions } from "../../../shared/hook/useProductGroupOptions";
import { personalization_groups } from "../../../shared/reference/personalization_groups";
import { normalizedLocale } from "../../../common/libs/normalizedLocale";
import i18n from "../../../i18n";

const toDisplayDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = parseISO(value);
  if (!isValid(parsed)) return value;
  return format(parsed, "dd.MM.yyyy HH:mm");
};

const toDisplayDate = (value?: string) => {
  if (!value) return "-";
  const parsed = parseISO(value);
  if (!isValid(parsed)) return value;
  return format(parsed, "dd.MM.yyyy");
};

const getStatusMeta = (status: string, t: (key: string) => string) => {
  switch (status) {
    case "FORMING":
      return { type: "test", label: t("marking_codes_report.status_forming") };
    case "FORMED":
      return { type: "active", label: t("marking_codes_report.status_formed") };
    case "ERROR":
      return { type: "error", label: t("marking_codes_report.status_error") };
    default:
      return { type: "", label: status || "-" };
  }
};

type PreviewColumn = {
  key: keyof MarkingCodeReportRow;
  labelKey: string;
  render?: (row: MarkingCodeReportRow, t: (key: string) => string) => string;
  // Колонки организации и местоположения — только если бэк вернул includeOrganizationColumns
  orgOnly?: boolean;
  // Персональные поля товарной группы — только для отчёта по этой ТГ (productGroupId)
  groupIds?: number[];
  cellClassName?: string;
};

const COL = "marking_codes_report.columns";

// productGroupId из справочника product_groups
const GROUP_JEWELRY = 1;
const GROUP_WOOD = 2;
const GROUP_DERIVATIVES = 4;

// Тип и порода древесины приходят кодами ("1", "2") — подписи из справочника персонализации
// на языке интерфейса
const woodOptionLabel = (field: "woodType" | "woodSpecies", raw?: string) => {
  if (!hasValue(raw)) return "-";
  const wood = personalization_groups.find((group: any) => group.code === "002") as any;
  const options = wood?.categories?.[0]?.fields?.find((f: any) => f.value === field)?.options ?? [];
  const option = options.find((o: any) => String(o.value) === String(raw));
  const isKk = normalizedLocale(i18n.language) === "kk";
  return (isKk && option?.labelKz) || option?.label || String(raw);
};

const dateColumn = (
  key: keyof MarkingCodeReportRow,
  labelKey: string,
): PreviewColumn => ({
  key,
  labelKey,
  render: (row) => toDisplayDate(row[key] as string | undefined),
});

// Порядок и названия — как в выгрузке отчёта в Excel
const PREVIEW_COLUMNS: PreviewColumn[] = [
  { key: "organizationIinBin", labelKey: `${COL}.org_iin_bin`, orgOnly: true },
  { key: "organizationName", labelKey: `${COL}.org_name`, orgOnly: true },
  { key: "regionCode", labelKey: `${COL}.region_code`, orgOnly: true },
  { key: "districtCode", labelKey: `${COL}.district_code`, orgOnly: true },
  { key: "cityCode", labelKey: `${COL}.city_code`, orgOnly: true },
  { key: "participantType", labelKey: `${COL}.participant_type` },
  { key: "code", labelKey: `${COL}.code`, cellClassName: "min-w-[260px] break-all" },
  {
    key: "packagingType",
    labelKey: `${COL}.packaging_type`,
    render: (row, t) => {
      const labelKey =
        PACKAGING_LABEL_KEYS[row.packagingType as MarkingCodesPackagingType];
      return labelKey ? t(labelKey) : row.packagingType || "-";
    },
  },
  { key: "productTypeCode", labelKey: `${COL}.product_type_code` },
  { key: "productName", labelKey: `${COL}.product_name` },
  { key: "ntin", labelKey: `${COL}.ntin` },
  { key: "ntinName", labelKey: `${COL}.ntin_name` },
  {
    key: "status",
    labelKey: "marking_codes_report.km_status",
    render: (row, t) => {
      const labelKey = KM_STATUS_LABEL_KEYS[row.status as MarkingCodesKmStatus];
      return labelKey ? t(labelKey) : row.status || "-";
    },
  },
  dateColumn("orderDate", `${COL}.order_date`),
  { key: "orderNumber", labelKey: "marking_codes_report.order_number" },
  dateColumn("personalizationDate", `${COL}.personalization_date`),
  { key: "personalizationNumber", labelKey: `${COL}.personalization_number` },
  dateColumn("withdrawalDate", `${COL}.withdrawal_date`),
  { key: "withdrawalBin", labelKey: `${COL}.withdrawal_bin` },
  { key: "withdrawalName", labelKey: `${COL}.withdrawal_name` },
  { key: "transferActNumber", labelKey: `${COL}.transfer_act_number` },
  { key: "transferActReason", labelKey: `${COL}.transfer_act_reason` },
  { key: "transferActReceiverBin", labelKey: `${COL}.transfer_act_receiver_bin` },
  { key: "transferActReceiverName", labelKey: `${COL}.transfer_act_receiver_name` },
  { key: "hornLength", labelKey: `${COL}.horn_length`, groupIds: [GROUP_DERIVATIVES] },
  { key: "hornSort", labelKey: `${COL}.horn_sort`, groupIds: [GROUP_DERIVATIVES] },
  { key: "originSource", labelKey: `${COL}.origin_source`, groupIds: [GROUP_DERIVATIVES] },
  { key: "mainMaterial", labelKey: `${COL}.main_material`, groupIds: [GROUP_JEWELRY] },
  { key: "assay", labelKey: `${COL}.assay`, groupIds: [GROUP_JEWELRY] },
  { key: "trademark", labelKey: `${COL}.trademark`, groupIds: [GROUP_JEWELRY] },
  { key: "weight", labelKey: `${COL}.weight`, groupIds: [GROUP_JEWELRY] },
  { key: "size", labelKey: `${COL}.size`, groupIds: [GROUP_JEWELRY] },
  {
    key: "woodType",
    labelKey: `${COL}.wood_type`,
    groupIds: [GROUP_WOOD],
    render: (row) => woodOptionLabel("woodType", row.woodType),
  },
  {
    key: "woodSpecies",
    labelKey: `${COL}.wood_species`,
    groupIds: [GROUP_WOOD],
    render: (row) => woodOptionLabel("woodSpecies", row.woodSpecies),
  },
  { key: "loggingTicketNumber", labelKey: `${COL}.logging_ticket_number`, groupIds: [GROUP_WOOD] },
  { key: "woodVolume", labelKey: `${COL}.wood_volume`, groupIds: [GROUP_WOOD] },
];

function hasValue(value: unknown) {
  return value !== null && value !== undefined && value !== "";
}

const PREVIEW_MAX_PAGE_SIZE = 100;

const downloadBlob = (blob: Blob, fileName: string, mime: string) => {
  const fileBlob = blob.type ? blob : new Blob([blob], { type: mime });
  const url = window.URL.createObjectURL(fileBlob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

function MarkingCodesStatusReportDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const [report, setReport] = useState<MarkingCodesReportItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const pageSize = useAppSelector((state) => state.userSettings.pageSize);
  const productGroups = useProductGroupOptions();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [preview, setPreview] =
    useState<MarkingCodesReportPreviewResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewError, setIsPreviewError] = useState(false);

  const loadReport = async (id: number) => {
    try {
      const data = await getMarkingCodesReport(id);
      setReport(data);
      return data;
    } catch (error) {
      console.error("Error loading marking codes report:", error);
      setReport(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!reportId || !/^\d+$/.test(reportId)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    loadReport(Number(reportId));
  }, [reportId]);

  useEffect(() => {
    if (!reportId || report?.status !== "FORMING") return;
    const timer = window.setInterval(() => {
      loadReport(Number(reportId));
    }, 2500);
    return () => window.clearInterval(timer);
  }, [reportId, report?.status]);

  const hasRows = report?.status === "FORMED" && Boolean(report.rowsCount);

  // Предпросмотр грузим по фильтрам отчёта, когда он сформирован и в нём есть строки
  useEffect(() => {
    if (!report || !hasRows) return;
    setIsPreviewLoading(true);
    setIsPreviewError(false);
    previewMarkingCodesReport(
      {
        ...report.filters,
        productGroupId: report.filters?.productGroupId ?? report.productGroupId,
      },
      pagination.pageIndex + 1,
      Math.min(pagination.pageSize, PREVIEW_MAX_PAGE_SIZE),
    )
      .then(setPreview)
      .catch((error) => {
        console.error("Error loading marking codes report preview:", error);
        setPreview(null);
        setIsPreviewError(true);
      })
      .finally(() => setIsPreviewLoading(false));
  }, [report?.id, hasRows, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target as Node)
      ) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownload = async (format: "XLSX" | "PDF") => {
    if (!report?.id) return;
    setIsExporting(true);
    try {
      const blob = await downloadMarkingCodesReport(report.id, format);
      const baseName =
        report.reportNumber || `marking-codes-report-${report.id}`;
      if (format === "XLSX") {
        downloadXlsxBlob(blob, `${baseName}.xlsx`);
      } else {
        downloadBlob(blob, `${baseName}.pdf`, "application/pdf");
      }
    } catch (error) {
      console.error("Error downloading marking codes report:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setIsExporting(false);
      setIsExportOpen(false);
    }
  };

  if (isLoading) return <Loader />;

  if (!report) {
    return (
      <div className="flex flex-1 flex-col min-w-0 p-4">
        <EmptyListPlaceholderContent
          title={t("marking_codes_report.load_error")}
        />
        <button
          type="button"
          className="mt-4 self-start bg-bg text-gray-600 font-bold py-2 px-4 rounded"
          onClick={() => navigate("/reports/marking-codes-status")}
        >
          {t("billing.close")}
        </button>
      </div>
    );
  }

  const st = getStatusMeta(report.status, t);
  const canDownload = report.status === "FORMED";

  const reportGroupId = report.filters?.productGroupId ?? report.productGroupId;
  const productGroupName = productGroups.nameById(reportGroupId);
  const hasPeriod = Boolean(report.filters?.dateFrom || report.filters?.dateTo);

  const previewRows = preview?.rows ?? [];
  const visibleColumns = PREVIEW_COLUMNS.filter((column) => {
    if (column.orgOnly && !preview?.includeOrganizationColumns) return false;
    if (column.groupIds) {
      return reportGroupId != null && column.groupIds.includes(reportGroupId);
    }
    return true;
  });
  // Пока ответа нет (включая первый рендер до старта запроса) — показываем лоадер вместо таблицы
  const isPreviewPending = isPreviewLoading || (!preview && !isPreviewError);

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">
        {t("marking_codes_report.title")}
      </h2>

      {report.status === "FORMING" && (
        <div className="bg-white rounded-xl shadow-md p-8 mb-5 flex flex-col items-center justify-center gap-3">
          <div className="w-10 h-10 border-4 border-green/30 border-t-green rounded-full animate-spin" />
          <p className="text-sm text-gray-600">
            {t("marking_codes_report.status_forming")}...
          </p>
        </div>
      )}

      {report.status === "ERROR" && (
        <div className="bg-white rounded-xl shadow-md p-5 mb-5">
          <EmptyListPlaceholderContent
            title={
              report.errorMessage || t("marking_codes_report.status_error")
            }
          />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-5">
        <div className="flex flex-row items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              <span className="font-semibold">
                {t("marking_codes_report.report_number")}:
              </span>{" "}
              {report.reportNumber || `№ ${report.id}`}
            </p>
            <p>
              <span className="font-semibold">
                {t("marking_codes_report.status")}:
              </span>{" "}
              <span className="inline-flex align-middle ml-1">
                <StatusBadge type={st.type} fontSize="14px">
                  {st.label}
                </StatusBadge>
              </span>
            </p>
            <p>
              <span className="font-semibold">
                {t("marking_codes_report.generated_at")}:
              </span>{" "}
              {toDisplayDateTime(
                report.completedAt || report.requestedAt || report.startedAt,
              )}
            </p>
            <p>
              <span className="font-semibold">
                {t("participants_registry_report.product_group")}:
              </span>{" "}
              {productGroupName}
            </p>
            <p>
              <span className="font-semibold">
                {t("billing.reportPeriod")}:
              </span>{" "}
              {hasPeriod
                ? `${toDisplayDate(report.filters?.dateFrom)} — ${toDisplayDate(report.filters?.dateTo)}`
                : t("marking_codes_report.period_all")}
            </p>
            {report.requestedByName && (
              <p>
                <span className="font-semibold">
                  {t("marking_codes_report.executor")}:
                </span>{" "}
                {report.requestedByName}
              </p>
            )}
            {report.rowsCount != null && (
              <p>
                <span className="font-semibold">
                  {t("marking_codes_report.rows_count")}:
                </span>{" "}
                {report.rowsCount}
              </p>
            )}
          </div>

          <div className="flex flex-row gap-2 items-start">
            <div className="relative" ref={exportMenuRef}>
              <button
                type="button"
                className="bg-green text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                disabled={!canDownload || isExporting}
                onClick={() => setIsExportOpen((prev) => !prev)}
              >
                {t("mainButtons.download")}
              </button>
              {isExportOpen && canDownload && (
                <div className="absolute right-0 mt-2 min-w-[140px] bg-white rounded-lg shadow-lg z-50 overflow-hidden border border-gray-100">
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                    onClick={() => handleDownload("XLSX")}
                  >
                    Excel
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                    onClick={() => handleDownload("PDF")}
                  >
                    PDF
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => navigate("/reports/marking-codes-status")}
            >
              {t("billing.close")}
            </button>
          </div>
        </div>

        {report.status === "FORMED" && (!report.rowsCount || report.rowsCount === 0) && (
          <EmptyListPlaceholderContent
            title={t("marking_codes_report.no_data_for_filters")}
          />
        )}

        {hasRows && isPreviewPending && (
          <div className="flex flex-col items-center justify-center gap-3 py-10">
            <div className="w-10 h-10 border-4 border-green/30 border-t-green rounded-full animate-spin" />
            <p className="text-sm text-gray-600">{t("billing.loading")}</p>
          </div>
        )}

        {hasRows && !isPreviewPending && previewRows.length === 0 && (
          <EmptyListPlaceholderContent
            title={
              isPreviewError
                ? t("marking_codes_report.preview_error")
                : t("marking_codes_report.no_data_for_filters")
            }
          />
        )}

        {hasRows && !isPreviewPending && previewRows.length > 0 && (
          <>
            <div className="rounded-lg overflow-x-auto">
              <table className="max-w-full w-full leading-normal">
                <thead className="border-b-2 border-gray-300">
                  <tr>
                    <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                      №
                    </th>
                    {visibleColumns.map((column) => (
                      <th
                        key={column.key}
                        className="px-5 py-3 min-w-[140px] text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                      >
                        {t(column.labelKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-normal text-center">
                  {previewRows.map((row, index) => (
                    <tr key={`km-row-${row.number ?? index}-${row.code ?? ""}`}>
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-600">
                        {row.number ??
                          pagination.pageIndex * pagination.pageSize + index + 1}
                      </td>
                      {visibleColumns.map((column) => {
                        const raw = row[column.key];
                        const value = column.render
                          ? column.render(row, t)
                          : hasValue(raw)
                            ? String(raw)
                            : "-";
                        return (
                          <td
                            key={column.key}
                            className={`px-5 py-4 text-sm border-b border-gray-200 text-gray-600 ${column.cellClassName ?? ""}`}
                          >
                            {value}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={preview?.totalItems || 0}
              totalPages={preview?.totalPages || 0}
              pagination={pagination}
              setPagination={setPagination}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default MarkingCodesStatusReportDetailsPage;
